import React, { useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    CircularProgress,
    Typography,
} from "@mui/material";
import { PaymentMethod } from "../../types";
import { AddPaymentRequest } from "../../services/orderService";
import { formatCurrencyBDT } from "../../utils/currency";
import { AccountPicker } from "../accounts/AccountPicker";
import { ConfirmUntaggedPaymentDialog } from "../accounts/ConfirmUntaggedPaymentDialog";
import {
    ORDER_PAYMENT_METHODS,
    PAYMENT_METHOD_LABELS,
} from "../../utils/paymentMethods";

interface AddPaymentDialogProps {
    open: boolean;
    onClose: () => void;
    onConfirm: (data: AddPaymentRequest) => Promise<void>;
    amountDue: number;
}

const defaultForm = (): AddPaymentRequest => ({
    amount: 0,
    payment_method: "CASH",
    reference_number: "",
    notes: "",
});

export const AddPaymentDialog: React.FC<AddPaymentDialogProps> = ({
    open,
    onClose,
    onConfirm,
    amountDue,
}) => {
    const [form, setForm] = useState<AddPaymentRequest>(defaultForm);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [confirmUntaggedOpen, setConfirmUntaggedOpen] = useState(false);

    const handleOpen = () => {
        setForm({ ...defaultForm(), amount: amountDue > 0 ? amountDue : 0 });
        setError(null);
        setConfirmUntaggedOpen(false);
    };

    const handleClose = () => {
        if (saving) return;
        setForm(defaultForm());
        setError(null);
        onClose();
    };

    const submitPayment = async () => {
        setSaving(true);
        setError(null);
        try {
            await onConfirm({
                amount: form.amount,
                payment_method: form.payment_method,
                reference_number: form.reference_number || undefined,
                notes: form.notes || undefined,
                account_id: form.account_id || null,
            });
            setForm(defaultForm());
            onClose();
        } catch (err: any) {
            setError(err?.message || "Failed to add payment. Please try again.");
        } finally {
            setSaving(false);
        }
    };

    const handleConfirm = () => {
        if (form.amount <= 0) {
            setError("Amount must be greater than 0.");
            return;
        }
        if (!form.account_id) {
            setConfirmUntaggedOpen(true);
            return;
        }
        submitPayment();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            TransitionProps={{ onEnter: handleOpen }}
        >
            <DialogTitle>Add Payment</DialogTitle>
            <DialogContent>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
                    <TextField
                        label="Amount"
                        type="number"
                        value={form.amount}
                        onChange={(e) =>
                            setForm({ ...form, amount: Number(e.target.value) })
                        }
                        fullWidth
                        required
                        inputProps={{ min: 0, step: 0.01 }}
                        helperText={`Amount due: ${formatCurrencyBDT(amountDue)}`}
                        error={!!error && form.amount <= 0}
                    />
                    <TextField
                        select
                        label="Payment Method"
                        value={form.payment_method}
                        onChange={(e) =>
                            setForm({
                                ...form,
                                payment_method: e.target.value as PaymentMethod,
                            })
                        }
                        fullWidth
                        required
                    >
                        {ORDER_PAYMENT_METHODS.map((method) => (
                            <MenuItem key={method} value={method}>
                                {PAYMENT_METHOD_LABELS[method]}
                            </MenuItem>
                        ))}
                    </TextField>
                    <AccountPicker
                        value={form.account_id}
                        onChange={(account_id) => setForm({ ...form, account_id })}
                        helperText="Which cash/bank account this payment landed in"
                    />
                    <TextField
                        label="Reference Number (Optional)"
                        value={form.reference_number}
                        onChange={(e) =>
                            setForm({ ...form, reference_number: e.target.value })
                        }
                        fullWidth
                        placeholder="Transaction ID, check number, etc."
                    />
                    <TextField
                        label="Notes (Optional)"
                        value={form.notes}
                        onChange={(e) =>
                            setForm({ ...form, notes: e.target.value })
                        }
                        fullWidth
                        multiline
                        rows={2}
                        placeholder="Additional payment details..."
                    />
                    {error && (
                        <Typography variant="body2" color="error">
                            {error}
                        </Typography>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    onClick={handleConfirm}
                    variant="contained"
                    disabled={saving || form.amount <= 0}
                    startIcon={saving ? <CircularProgress size={16} /> : undefined}
                >
                    {saving ? "Saving..." : "Add Payment"}
                </Button>
            </DialogActions>

            <ConfirmUntaggedPaymentDialog
                open={confirmUntaggedOpen}
                amount={form.amount}
                confirmLabel="Add Payment"
                onCancel={() => setConfirmUntaggedOpen(false)}
                onConfirm={() => {
                    setConfirmUntaggedOpen(false);
                    submitPayment();
                }}
            />
        </Dialog>
    );
};
